import { Icon } from "@chakra-ui/react";
import { EmrActions } from "@medstonetech/slate-icons";
import { TriageChartCode } from "enums";
import {
  ChartRow,
  ChartRowElement,
  ChartRowGroup,
  ChartSection,
  ChartSectionHeader,
  ChartSubsection,
  ChartSubsectionContent,
  ChartSubsectionHeader,
} from "modules";
import * as React from "react";
import { FormProvider } from "react-hook-form";
import { Loading, StaffAvatar } from "shared";
import { useTriageChartActionsForm } from "../../hooks";
import { ActionsHeader } from "../../shared";
import { actionsConstants } from "../../shared/actions";
import { usePermissions } from "contexts";

const sectionId = "SACT";
const CHART_CODE: TriageChartCode = "T003x";

function N03Actions() {
  const {
    getIsLoading,
    onSubmit,
    updateIsLoading,
    encounterId,
    useFormReturn,
    onSaveAndNext,
  } = useTriageChartActionsForm({ chartCode: CHART_CODE });
  const { scope } = usePermissions();
  const isReadOnly = !scope("nurse:actions").isEditable;

  const { getValues } = useFormReturn;
  const initialActionsSections = getValues("initialActionsSections");
  const actionsSections = getValues("actionsSections");

  if (getIsLoading) {
    return <Loading />;
  }

  return (
    <FormProvider {...useFormReturn}>
      <ChartSection
        onSubmit={onSubmit}
        disabled={isReadOnly}
        onRouteChangeSave={(onComplete) => {
          onSubmit();
          onComplete();
        }}
      >
        <ChartSectionHeader
          icon={<Icon as={EmrActions} />}
          showNotesBtn={false}
          showActionsBtns
          isLoading={updateIsLoading}
          onSaveAndNext={onSaveAndNext}
        >
          Actions {isReadOnly ? "(Read Only)" : ""}
        </ChartSectionHeader>
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn={false}
              encounterId={encounterId}
              chartId={CHART_CODE}
              sectionId="SACT"
              subsectionId="Q001"
              subsectionLabel="Initial Actions"
            >
              Initial Actions
            </ChartSubsectionHeader>
          }
          content={
            <>
              <ActionsHeader />
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowElement
                    type="date"
                    name={`initialActionsSections[0].${CHART_CODE}${sectionId}Q001A000`}
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="ID"
                      name={`initialActionsSections[0].${CHART_CODE}${sectionId}Q001A001`}
                    />
                    <ChartRowElement
                      label="*"
                      type="label"
                      pl="unset"
                      ml="8px"
                      marginInline="unset"
                      color="red"
                      fontWeight="600"
                      justifyContent="flex-start"
                    />
                    <ChartRowElement
                      pl="20px"
                      type="option"
                      label="Allergy band"
                      name="initialActionsSections[0].T001xSACTQ001A002"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="applied"
                      name={`initialActionsSections[0].${CHART_CODE}${sectionId}Q001A003`}
                    />
                    <ChartRowElement
                      label="*"
                      type="label"
                      pl="unset"
                      ml="8px"
                      marginInline="unset"
                      color="red"
                      fontWeight="600"
                      justifyContent="flex-start"
                    />
                    <ChartRowElement
                      type="option"
                      label="verified"
                      name="initialActionsSections[0].T001xSACTQ001A004"
                    />
                    <ChartRowElement
                      label="*"
                      type="label"
                      pl="unset"
                      ml="8px"
                      marginInline="unset"
                      color="red"
                      fontWeight="600"
                      justifyContent="flex-start"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[0] &&
                          initialActionsSections[0].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[0] &&
                          initialActionsSections[0].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="initialActionsSections[1].date"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="c-collar"
                      name="initialActionsSections[1].T001xSACTQ001A005"
                    />
                    <ChartRowElement
                      type="option"
                      label="backboard"
                      name="initialActionsSections[1].T001xSACTQ001A006"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="applied"
                      name="initialActionsSections[1].T001xSACTQ001A007"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[1] &&
                          initialActionsSections[1].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[1] &&
                          initialActionsSections[1].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="initialActionsSections[2].date"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="disrobed"
                      name="initialActionsSections[2].T003xSACTQ001A008"
                    />
                    <ChartRowElement
                      type="option"
                      label="gowned"
                      name="initialActionsSections[2].T001xSACTQ001A009"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="blanket provided"
                      name="initialActionsSections[2].T001xSACTQ001A010"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[2] &&
                          initialActionsSections[2].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[2] &&
                          initialActionsSections[2].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="initialActionsSections[3].date"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="ice pack"
                      name="initialActionsSections[3].T001xSACTQ001A011"
                    />

                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="elevation"
                      name="initialActionsSections[3].T001xSACTQ001A012"
                    />
                    <ChartRowElement
                      type="option"
                      label="immobilization"
                      name="initialActionsSections[3].T001xSACTQ001A013"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[3] &&
                          initialActionsSections[3].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[3] &&
                          initialActionsSections[3].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="initialActionsSections[4].date"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="bandage"
                      name="initialActionsSections[4].T001xSACTQ001A014"
                    />

                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="dressing"
                      name="initialActionsSections[4].T001xSACTQ001A015"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[4] &&
                          initialActionsSections[4].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[4] &&
                          initialActionsSections[4].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="initialActionsSections[5].date"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="bed low position"
                      name="initialActionsSections[5].T001xSACTQ001A016"
                    />

                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="side rails up"
                      name="initialActionsSections[5].T001xSACTQ001A017"
                    />
                    <ChartRowElement
                      type="option"
                      label="x 1"
                      name="initialActionsSections[5].T001xSACTQ001A018"
                    />
                    <ChartRowElement
                      type="option"
                      label="x 2"
                      name="initialActionsSections[5].T003xSACTQ001A019"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[5] &&
                          initialActionsSections[5].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[5] &&
                          initialActionsSections[5].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="initialActionsSections[6].date"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="call light in reach"
                      name="initialActionsSections[6].T003xSACTQ001A020"
                    />

                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="head of bed elevated"
                      name="initialActionsSections[6].T003xSACTQ001A021"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[6] &&
                          initialActionsSections[6].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[6] &&
                          initialActionsSections[6].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowGroup>
                    <ChartRowElement
                      type="date"
                      name="initialActionsSections[7].date"
                    />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="narcotic speech given"
                      name="initialActionsSections[7].T003xSACTQ001A022"
                    />

                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="medication speech given"
                      name="initialActionsSections[7].T003xSACTQ001A023"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[7] &&
                          initialActionsSections[7].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[7] &&
                          initialActionsSections[7].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
            </>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn={false}
              encounterId={encounterId}
              chartId={CHART_CODE}
              sectionId="SACT"
              subsectionId="Q002"
              subsectionLabel="Actions"
            >
              Actions
            </ChartSubsectionHeader>
          }
          content={
            <>
              <ActionsHeader />
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement type="date" name="actionsSections[0].date" />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="pulse checked with doppler"
                      name="actionsSections[0].T003xSACTQ002A001"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[0] &&
                          actionsSections[0].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[0] &&
                          actionsSections[0].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement type="date" name="actionsSections[1].date" />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="cleaned wound"
                      name="actionsSections[1].T003xSACTQ002A002"
                    />
                    <ChartRowElement
                      type="option"
                      label="gauze"
                      name="actionsSections[1].T003xSACTQ002A003"
                    />
                    <ChartRowElement
                      type="option"
                      label="pressure dressing"
                      name="actionsSections[1].T003xSACTQ002A004"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[1] &&
                          actionsSections[1].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[1] &&
                          actionsSections[1].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement type="date" name="actionsSections[2].date" />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="cardiac monitor"
                      name="actionsSections[2].T003xSACTQ002A005"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[2] &&
                          actionsSections[2].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[2] &&
                          actionsSections[2].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement type="date" name="actionsSections[3].date" />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="pulse oximeter"
                      name="actionsSections[3].T003xSACTQ002A006"
                    />
                    <ChartRowElement
                      type="input"
                      name="actionsSections[3].T003xSACTQ002A007"
                      size="sm"
                      inputType="number"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[3].T003xSACTQ002A024"
                      label="%"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[3].T003xSACTQ002A025"
                      label="RA/"
                    />
                    <ChartRowElement
                      type="input"
                      name="actionsSections[3].T003xSACTQ002A008"
                      size="md"
                      inputType="number"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[3].T003xSACTQ002A026"
                      label="L"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[3] &&
                          actionsSections[3].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[3] &&
                          actionsSections[3].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement type="date" name="actionsSections[4].date" />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="O2"
                      name="actionsSections[4].T003xSACTQ002A009"
                    />
                    <ChartRowElement
                      type="input"
                      name="actionsSections[4].T003xSACTQ002A010"
                      size="sm"
                      inputType="number"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[4].T003xSACTQ002A027"
                      label="L"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[4].T003xSACTQ002A028"
                      label="via"
                    />
                    <ChartRowElement
                      type="option"
                      label="NC"
                      name="actionsSections[4].T003xSACTQ002A011"
                    />
                    <ChartRowElement
                      type="option"
                      label="mask"
                      name="actionsSections[4].T003xSACTQ002A012"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[4] &&
                          actionsSections[4].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[4] &&
                          actionsSections[4].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement type="date" name="actionsSections[5].date" />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="bedside glucose"
                      name="actionsSections[5].T003xSACTQ002A013"
                    />
                    <ChartRowElement
                      type="input"
                      name="actionsSections[5].T003xSACTQ002A014"
                      size="md"
                      inputType="number"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[5].T003xSACTQ002A029"
                      label="mg/dL"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[5] &&
                          actionsSections[5].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[5] &&
                          actionsSections[5].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement type="date" name="actionsSections[6].date" />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="ready for provider eval"
                      name="actionsSections[6].T003xSACTQ002A015"
                    />
                    <ChartRowElement
                      type="option"
                      label="notified provider"
                      name="actionsSections[6].T003xSACTQ002A016"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[6] &&
                          actionsSections[6].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[6] &&
                          actionsSections[6].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement type="date" name="actionsSections[7].date" />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="c-collar"
                      name="actionsSections[7].T003xSACTQ002A017"
                    />
                    <ChartRowElement
                      type="option"
                      label="backboard"
                      name="actionsSections[7].T003xSACTQ002A018"
                    />
                    <ChartRowElement
                      type="option"
                      label="removed"
                      name="actionsSections[7].T003xSACTQ002A019"
                    />
                    <ChartRowElement
                      type="option"
                      label="cleared by"
                      name="actionsSections[7].T003xSACTQ002A020"
                    />
                    <ChartRowElement
                      type="input"
                      name="actionsSections[7].T003xSACTQ002A021"
                    />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[7] &&
                          actionsSections[7].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[7] &&
                          actionsSections[7].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement type="date" name="actionsSections[8].date" />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="restraints"
                      name="actionsSections[8].T003xSACTQ002A022"
                    />
                    <ChartRowElement
                      type="option"
                      label="see documentation"
                      name="actionsSections[8].T003xSACTQ002A023"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      maxWidth="32px"
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[8] &&
                          actionsSections[8].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[8] &&
                          actionsSections[8].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
            </>
          }
        />
      </ChartSection>
    </FormProvider>
  );
}

export { N03Actions };
